/**
 * EnrichData — the emails, their provenance and any enrichment failure, shared by Import Detail's
 * `ContactsTable` and `ExportToThrivePanel` (epic
 * 2026-07-13-contact-enrichment-hellosky, per-user design review 2026-07-15).
 *
 * Renamed/redesigned from `ContactEmails` (subtask 04, which rendered a
 * compact second line under the name cell): now its own labeled column —
 * "Personal"/"Work" lines each with a provenance badge ("via nu_sync" /
 * "via HelloSky"), and an INLINE warning line (not an icon+tooltip) when
 * `enrichment_status === "failed"`, so the failure reason is visible without
 * a hover. "—" when there's nothing to show and the lookup didn't fail.
 *
 * Takes just the enrichment-field subset (structural, not `ImportContactRow`
 * itself) so both consumers can pass it directly against either
 * `ImportContactRow` or `ContactExportResult` — the two contracts carry
 * these 6 fields with identical shapes/types, so no adapter object is
 * needed at the call site.
 *
 * Tokens only — no inline `style={{}}`, no arbitrary `text-[Npx]`.
 */
import type { ImportContactRow } from "@nu-assist/contracts";
import { AlertTriangle } from "lucide-react";

import { SOURCE_BADGE_BY_VALUE, enrichmentErrorCopy } from "./enrichmentDefs";

export type EnrichmentFields = Pick<
  ImportContactRow,
  | "email"
  | "email_source"
  | "work_email"
  | "work_email_source"
  | "enrichment_status"
  | "enrichment_error"
>;

function SourceBadge({ source }: { source: ImportContactRow["email_source"] }) {
  const def = source ? SOURCE_BADGE_BY_VALUE[source] : undefined;
  // csv / manual / thrive_match / rocketreach carry no badge — only the two
  // enrichment sources (nu_sync, hellosky) are worth calling out.
  if (!def) return null;
  return (
    <span
      data-testid="email-source-badge"
      data-source={source}
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-micro font-bold ${def.pillClass}`}
    >
      {def.label}
    </span>
  );
}

function EmailLine({
  label,
  email,
  source,
  clip,
}: {
  label: string;
  email: string;
  source: ImportContactRow["email_source"];
  clip: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      {/* Label and provenance badge sit ABOVE the address, not beside it, so
          the address gets the cell's FULL width. Measured with them inline:
          the label (56px) plus a "via HelloSky" badge (77px) plus gaps took
          145px of a 264px cell, leaving 119px — which wrapped a 42-character
          address onto 3 lines inside a column that was supposed to be wide
          enough for it. Column width alone cannot fix that; the width has to
          reach the text. */}
      {/* `flex-wrap` + `whitespace-nowrap` on the label. Each fixes a different
          thing, both measured at the column's 128px clamp:

          `whitespace-nowrap` stops the LABEL shredding. Without it "Personal"
          rendered as "Per/son/al" over three lines — the badge is `shrink-0`
          and the label was not, so the label absorbed the whole squeeze, and
          `[overflow-wrap:anywhere]` (inherited from the container) let it break
          mid-word. The address stopped shredding and the label started.

          `flex-wrap` stops the BADGE being clipped. With the label held on one
          line the row no longer fits, and measured without wrapping the badge
          ran 11px past the cell's right edge — 67px of 77px visible, row
          `scrollWidth` 126 vs `clientWidth` 104. Wrapping drops it to its own
          line intact.

          NOT the reason, though a first draft of this comment said so: neither
          rule is needed to keep the column reachable at 128px. This column is
          PINNED (`min == max == width`), so its width is not derived from
          min-content at all — measured, it reaches the clamp either way. Every
          "the wrap rule protects the drag" argument in this file has now been
          tested and only ONE survived, on the address itself (128px with the
          rules, 140px without). Test before you write that sentence again. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="whitespace-nowrap text-text-muted">{label}</span>
        <SourceBadge source={source} />
      </div>
      {/* THE ADDRESS IS THE ONE FIELD THAT TRUNCATES (Lucas, 2026-08-21).
          cl.4 item 6 removed truncation everywhere; this is the single carve-
          out, and it is scoped to the address alone — the name, the
          company/title line, the annex rationale and the Company Map columns
          all still render in full.

          `line-clamp-1` and NOT `truncate`, and the difference is load-bearing
          rather than stylistic. `truncate` sets `white-space: nowrap`, which
          makes the address its own MIN-content width — and a `<th>` width
          holds under `table-layout: auto` only while every body cell's
          min-content stays at or below it.

          MEASURED, dragged past the clamp. `line-clamp-1` reaches **128px**
          and clips with an ellipsis on every dataset tried. `truncate` clips
          NOTHING — the column grows to fit instead — and where it stalls
          depends on the longest address in the column, which is the point:

            21-char address only ......... stalls at 140px
            42-char addresses throughout . stalls at 261px
            a 77-char work email present . stalls at 413px

          So checklist 7's drag is not degraded by `truncate`, it is defeated,
          and by an amount the data chooses. The exact bug `/pr-review` iter 1
          found, reintroduced by the fix for it.

          Two earlier drafts of this comment got the number wrong in different
          ways — one guessed "~260px", the next quoted 413 as if it followed
          from a 42-character address (it is the 77-character work email on the
          same row that produces it). `/pr-review` iter 3 measured all three.
          The lesson is the one this file keeps relearning: quote the dataset
          with the number, or the number is folklore.

          That is why the team's earlier interim clip (`89a76745`) needed
          `min-w-[6rem] max-w-xs truncate` — a nowrap address has to be capped
          by SOMETHING. But a fixed cap fights a resizable column: it clips at
          every width, including a wide one with room to spare, which is
          exactly what that PR's own review rejected.

          `line-clamp-1` has neither problem. It clips to one line box with an
          ellipsis WITHOUT setting `nowrap`, so `[overflow-wrap:anywhere]` below
          still governs min-content (measured on this branch: 1 character, which
          is what lets the pin survive a drag past the clamp). No cap is needed,
          so the address simply gets longer as the column is dragged wider.

          `title` carries the full address for HOVER. Not for assistive tech,
          though an earlier draft said so: `line-clamp` is a purely visual clip,
          the whole address stays in the DOM and the accessibility tree, and a
          screen reader reads it in full with or without `title` (which is
          generally not announced at all on a role-less span).

          The users actually served are mouse users. Keyboard-only and touch
          users get no hover, and the escape hatch is NOT equally real for both
          — an earlier revision of this comment claimed it was (`/pr-review`
          iter 8, Agent C). Keyboard users have one: the handle is
          `role="separator"` + `tabIndex={0}`, so `ArrowRight` widens the column.
          TOUCH users effectively do not: the grab strip is `w-1.5`, a 6px
          target, far under any touch-target minimum. For them the address is
          simply not fully readable, and that is a known cost of item 6's
          narrowing rather than something this comment should paper over.
          Stated because this is the only text in the table that is not fully
          readable on screen. */}
      <span
        className={`min-w-0 [overflow-wrap:anywhere] ${clip ? "line-clamp-1" : ""}`}
        title={clip ? email : undefined}
        data-testid="email-address"
      >
        {email}
      </span>
    </div>
  );
}

/**
 * The contact's emails, their provenance and any enrichment failure.
 *
 * Rendered by the contacts table's "Contact info" column (cl.4 item 8) and by
 * `ExportToThrivePanel`, in the same stacked shape for both.
 *
 * `clip` — OPT-IN, and off by default on purpose. Lucas's 2026-08-21 call
 * narrowed item 6 for "the email COLUMN"; the export panel is not a column,
 * it is the modal where a recruiter confirms which address is about to be
 * written to Thrive. Two reviewers raised the same objection independently:
 * in the table a clipped address is recoverable by dragging the column wider,
 * and that mitigation is the design's whole argument — the panel has no
 * resize affordance at all, so the same clip there is strictly worse. This
 * file has already cost one silent regression in that exact consumer (see the
 * typography note below), so the clip is opted into by the table rather than
 * inherited by everything.
 *
 * A revision of this file moved the whole block onto a third line under the
 * contact's name and deleted the column, which needed two layouts. The column
 * came back — cl.4 item 7 requires it as a base column — so the second layout
 * went with it. One consumer shape, one set of classes to reason about.
 */
export function EnrichData({ row, clip = false }: { row: EnrichmentFields; clip?: boolean }) {
  const failed = row.enrichment_status === "failed";
  const hasAnyEmail = Boolean(row.email) || Boolean(row.work_email);

  // Nothing to say — an em-dash, because a column that renders empty reads as
  // broken rather than as "no data".
  if (!hasAnyEmail && !failed) {
    return (
      <span data-testid="enrich-data-empty" className="text-caption text-text-muted">
        —
      </span>
    );
  }

  const personal = row.email ? (
    <EmailLine label="Personal" email={row.email} source={row.email_source} clip={clip} />
  ) : null;
  const work = row.work_email ? (
    <EmailLine label="Work" email={row.work_email} source={row.work_email_source} clip={clip} />
  ) : null;
  const warning = failed ? (
    <span
      data-testid="enrichment-warning"
      data-error={row.enrichment_error ?? undefined}
      className="inline-flex items-center gap-1 text-warning"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{enrichmentErrorCopy(row.enrichment_error)}</span>
    </span>
  ) : null;

  return (
    /* Typography lives on the container, not on `EmailLine`. It rode on the
       line's own root once, and making that root layout-aware silently moved
       the export panel's addresses and failure warning to body size in the
       default text colour — on the one screen where a recruiter confirms which
       address is about to be written to Thrive. */
    <div
      data-testid="enrich-data"
      className="flex min-w-0 flex-col gap-1.5 text-caption text-text-secondary [overflow-wrap:anywhere]"
    >
      {personal}
      {work}
      {warning}
    </div>
  );
}
